import { fetchAiSpaceStatus } from '@flowus/common/chat-ai/hooks/ai-limit-store';
import { resetAIStore } from '@flowus/common/chat-ai/hooks/store';
import { getBizTrackerPlatform } from '@flowus/common/get-tracker-platform';
import { isNumber } from 'lodash-es';
import type { FC } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useHistory } from 'react-router-dom';
import { LoadingContainer } from 'src/common/components/loading-container';
import { LOCAL_KEY_CREATE_FORM } from 'src/common/const';
import { request } from 'src/common/request';
import { getDomain } from 'src/common/utils/url-utils';
import * as Empty from 'src/components/empty';
import { ToSharedPage } from 'src/components/empty/to-shared-page';
import { Footer } from 'src/components/footer';
import { useFetchPage, useFetchRootPages } from 'src/hooks/page';
import { useFetchSharePages } from 'src/hooks/page/use-fetch-share-pages';
import { useCurrentSpace } from 'src/hooks/space';
import { getCurrentSpaceUsers } from 'src/hooks/space/use-current-space-users';
import { useCurrentUser } from 'src/hooks/user';
import { setAppUiState, usePageRootLoaded } from 'src/services/app';
import { bizTracker } from 'src/utils/biz-tracker';
import { useCheckChatPage, useGetPageId } from 'src/utils/getPageId';
import { useAddCollectionForm } from './hooks/block/use-add-collection-form';
import { fetchSubscriptionData } from './services/app/hook/subscription-data';
import { $pageMetasCache } from './services/page-metas';
import { $currentUserCache } from './services/user/current-user';
import { ViewPath } from './utils';
import { locationToUrl } from './utils/location-utils';
import { ChatPage } from './views/chat-page';
import { Main } from './views/main';
import { Header } from './views/main/header';
import { useRedirect } from './views/main/use-redirect';
import { SharePage } from './views/share';

export const Home: FC = () => {
  const pageId = useGetPageId();
  const fetchPage = useFetchPage();
  const [suspend, setSuspend] = useState(<LoadingContainer />);
  const currentUser = useCurrentUser();
  const isLogin = Boolean(currentUser.uuid);

  // 路由地址跳转;
  useEffect(() => {
    void (async () => {
      if (!isLogin && pageId) {
        await fetchPage(pageId, {
          prefetch: true,
        });
        const tag = $pageMetasCache[pageId]?.tag;
        switch (tag) {
          case 'NO_PERMISSION': {
            setSuspend(
              <>
                <Header emptyStyle />
                <Empty.NotShared />
                <Footer />
              </>
            );
            break;
          }
          case 'NOT_FOUND': {
            setSuspend(
              <>
                <Header emptyStyle />
                <Empty.NotFound />
                <Footer />
              </>
            );
            break;
          }
          case 'CACHE_EXPIRED': {
            setSuspend(<Content />);
            break;
          }
          case 'NEED_PAY':
          default: {
            /**
             * https://flowus.cn/74caa249-11a2-404a-97b9-048a8fd91a03
             * 加载页面卡片的时候，会自动跳到 login。
             * rootcause: 需要密码以及付费页面这里的tag就会不一样
             */
            if (!tag || tag === 'NEED_PAY' || tag === 'NEED_PASSWORD') {
              setSuspend(
                <>
                  <Header emptyStyle />
                  <ToSharedPage />
                </>
              );
            } else {
              setSuspend(<Content />);
            }
            break;
          }
        }
      } else {
        setSuspend(<Content />);
      }
    })();
  }, [fetchPage, isLogin, pageId]);

  return suspend;
};

const Content: FC = () => {
  const domain = getDomain();
  const redirect = useRedirect();
  const [loading, setLoading] = useState(Boolean(domain));
  const currentSpace = useCurrentSpace();
  const fetchSpaceRootPages = useFetchRootPages();
  const fetchSpaceSharePages = useFetchSharePages();
  const pageId = useGetPageId();
  const fetchPage = useFetchPage();
  const pageRootLoaded = usePageRootLoaded();
  const addCollectionForm = useAddCollectionForm();
  const history = useHistory();
  const isChatPage = useCheckChatPage();

  const initPage = useCallback(async () => {
    resetAIStore();

    if (domain && !__PRIVATE__) {
      const res = await request.infra.getPublicDataByDomain.raw({ domain });
      if (res.code === 200) {
        setAppUiState({ $publicHomePage: res.data.publicHomePage });
      }
      setAppUiState({ $pageRootLoaded: true });
      setLoading(false);
      return;
    }

    if (!currentSpace.uuid) {
      locationToUrl(ViewPath.login);
      return;
    }

    void fetchSpaceSharePages(currentSpace.uuid);
    void fetchAiSpaceStatus(currentSpace.uuid);
    // 这段代码为了并行请求，有点丑
    await Promise.all([
      fetchSpaceRootPages(currentSpace.uuid),
      (async () => {
        // H5 不需要加载
        if (isMobile) return;
        const res = await fetchSubscriptionData(currentSpace.uuid);
        res &&
          bizTracker.addCommonParams(
            {
              spaceID: currentSpace.uuid,
              space_version: currentSpace.planType,
              space_members: Object.keys(getCurrentSpaceUsers()).length,
              user_occupation: isNumber($currentUserCache.occupation)
                ? $currentUserCache.occupation
                : undefined,
              space_profession: res.teamInfo.industry,
              space_scale: res.teamInfo.size,
              ...getBizTrackerPlatform(),
            },
            true
          );
      })(),
    ]);

    // 判断是否需要创建表单
    if (localStorage.getItem(LOCAL_KEY_CREATE_FORM)) {
      localStorage.removeItem(LOCAL_KEY_CREATE_FORM);
      addCollectionForm({
        success: (viewId) => {
          void Promise.resolve().then(() => {
            history.push(`/${viewId}`);
            setAppUiState({ $pageRootLoaded: true });
            setLoading(false);
          });
        },
      });
      return;
    }

    await redirect();
    setAppUiState({ $pageRootLoaded: true });
    setLoading(false);
  }, [
    addCollectionForm,
    currentSpace.planType,
    currentSpace.uuid,
    domain,
    fetchSpaceRootPages,
    fetchSpaceSharePages,
    history,
    redirect,
  ]);

  useEffect(() => {
    if (pageId) {
      void fetchPage(pageId);
    }
    if (!pageRootLoaded) {
      void initPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageRootLoaded]);

  useEffect(() => {
    setAppUiState({ $pageRootLoaded: false });
  }, [domain, currentSpace.uuid]);

  if (loading || !pageRootLoaded) return <LoadingContainer fixed />;

  if (domain) {
    return <SharePage />;
  }

  if (isChatPage && isMobile) {
    return <ChatPage id={pageId} />;
  }

  return <Main />;
};
